import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { DailyCurrencyRateService } from 'src/app/services/daily-currency/daily-currency-rate.service';
import { CurrencyRateModalService } from 'src/app/services/currency-rate-modal/currency-rate-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-edit-currency-rate',
  templateUrl: './edit-currency-rate.component.html',
  styleUrls: ['./edit-currency-rate.component.scss']
})
export class EditCurrencyRateComponent extends DestructorComponent implements OnInit {

  @Output() currencyRateUpdated = new EventEmitter<void>();

  currencyRateForm!: FormGroup;
  isLoading = false;
  selectedCurrencyRate: any = null;

  constructor(
    private fb: FormBuilder,
    private _dailyCurrencyRateService: DailyCurrencyRateService,
    private _currencyRateModalService: CurrencyRateModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.currencyRateForm = this.fb.group({
      usd: ['', [Validators.required, Validators.min(0)]],
      eur: ['', [Validators.required, Validators.min(0)]],
      gbp: ['', [Validators.required, Validators.min(0)]]
    });
  }

  private setupModalListener(): void {
    this.subscriptions.push(
      this._currencyRateModalService.selectedCurrencyRate$.subscribe(currencyRate => {
        if (currencyRate) {
          this.selectedCurrencyRate = currencyRate;
          this.populateForm(currencyRate);
        } else {
          this.selectedCurrencyRate = null;
          this.resetForm();
        }
      })
    );

    // Listen for modal show event
    const modalElement = document.getElementById('kt_modal_edit_currency_rate');
    if (modalElement) {
      modalElement.addEventListener('show.bs.modal', () => {
        const currencyRate = this._currencyRateModalService.getSelectedCurrencyRate();
        if (currencyRate) {
          this.selectedCurrencyRate = currencyRate;
          this.populateForm(currencyRate);
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this.resetForm();
      });
    }
  }

  private populateForm(currencyRate: any): void {
    this.currencyRateForm.patchValue({
      usd: currencyRate.usd || '',
      eur: currencyRate.eur || '',
      gbp: currencyRate.gbp || ''
    });
  }

  onSubmit(): void {
    if (this.currencyRateForm.valid && !this.isLoading && this.selectedCurrencyRate) {
      this.isLoading = true;

      const formData = this.currencyRateForm.value;

      // Remove empty fields
      const submitData: any = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData[key] = formData[key];
        }
      });

      const currencyRateId = this.selectedCurrencyRate.doc_id || this.selectedCurrencyRate._id;

      this.subscriptions.push(
        this._dailyCurrencyRateService.UpdateData(currencyRateId, submitData).subscribe({
          next: (response: any) => {
            this.isLoading = false;
            if (response.status) {
              Swal.fire({
                title: 'Success!',
                text: 'Currency rate updated successfully.',
                icon: 'success',
                confirmButtonText: 'OK'
              }).then(() => {
                this.closeModal();
                this.currencyRateUpdated.emit();
              });
            } else {
              Swal.fire({
                title: 'Error!',
                text: response.message || 'Failed to update currency rate.',
                icon: 'error',
                confirmButtonText: 'OK'
              });
            }
          },
          error: (error: any) => {
            this.isLoading = false;
            Swal.fire({
              title: 'Error!',
              text: error.error?.message || 'An error occurred while updating currency rate.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        })
      );
    }
  }

  closeModal(): void {
    const modalElement = document.getElementById('kt_modal_edit_currency_rate');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
    this.resetForm();
  }

  private resetForm(): void {
    this.currencyRateForm.reset();
    this.selectedCurrencyRate = null;
    this.isLoading = false;
  }
}
